import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, User, Eye, Download, CheckCircle, XCircle, Trash2, Upload, Plus, X, Hash, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { AdminResearchTopicsTab } from './AdminResearchTopicsTab';
import RichTextEditor from '@/components/RichTextEditor';

const BACKEND_URL = '';
const CATEGORIES = ['Academic', 'Business', 'Case Study'];
const TAG_OPTIONS = ['Net Zero', 'Supply Chain', 'ESG', 'Carbon', 'Circular Economy', 'Renewables', 'Policy', 'Transport', 'Warehousing', 'Data & AI', 'Innovation'];

export function AdminResearchTab() {
  const token = sessionStorage.getItem('session_token');
  const [view, setView] = useState('docs');
  const [researchFilter, setResearchFilter] = useState('');
  const [researchDocs, setResearchDocs] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sourceMode, setSourceMode] = useState('file');  // 'file' or 'link'
  const [form, setForm] = useState({ title: '', abstract: '', category: 'Academic', tags: [], authors: '', year: '', month: '', institution: '', country: '', file: null, external_url: '' });
  // Edit mode state — admin can edit metadata of any research doc in-place.
  const [editingDoc, setEditingDoc] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setEditForm({
      title: doc.title || '',
      abstract: doc.abstract || '',
      category: doc.category || 'Academic',
      tags: doc.tags || [],
      authors: doc.authors || '',
      year: doc.year || '',
      month: doc.month || '',
      institution: doc.institution || '',
      country: doc.country || '',
    });
  };

  const toggleEditTag = (t) => {
    setEditForm(prev => ({
      ...prev,
      tags: prev.tags.includes(t) ? prev.tags.filter(x => x !== t) : prev.tags.length < 6 ? [...prev.tags, t] : prev.tags,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingDoc || !editForm) return;
    if (!editForm.title.trim()) { toast.error('Title is required'); return; }
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        title: editForm.title.trim(),
        abstract: editForm.abstract.trim(),
        authors: editForm.authors.trim(),
        institution: editForm.institution.trim(),
        country: editForm.country.trim(),
        year: editForm.year ? Number(editForm.year) : null,
      };
      const res = await fetch(`${BACKEND_URL}/api/learning/${editingDoc.doc_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      toast.success('Research updated');
      setEditingDoc(null);
      setEditForm(null);
      loadResearch(researchFilter);
    } catch (e) { toast.error(e.message); }
    setSavingEdit(false);
  };

  useEffect(() => { loadResearch(''); }, []);

  const loadResearch = async (status) => {
    try {
      const params = status ? `?status=${status}&limit=100` : '?limit=100';
      const res = await fetch(`${BACKEND_URL}/api/admin/learning${params}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setResearchDocs(d.docs || []); }
    } catch {}
  };

  const handleFilterChange = (value) => {
    setResearchFilter(value);
    loadResearch(value);
  };

  const handleAction = async (docId, action) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/learning/${docId}/review`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action })
      });
      toast.success(`Document ${action === 'approve' ? 'approved' : 'rejected'}`);
      setResearchDocs(prev => prev.filter(d => d.doc_id !== docId));
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (docId) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/learning/${docId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Document deleted');
      setResearchDocs(prev => prev.filter(d => d.doc_id !== docId));
    } catch { toast.error('Failed'); }
  };

  const toggleTag = (t) => {
    setForm(prev => ({
      ...prev,
      tags: prev.tags.includes(t) ? prev.tags.filter(x => x !== t) : prev.tags.length < 6 ? [...prev.tags, t] : prev.tags,
    }));
  };

  const handleUpload = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (form.tags.length === 0) { toast.error('Select at least one tag'); return; }

    setUploading(true);
    try {
      let res;
      if (sourceMode === 'link') {
        const url = form.external_url.trim();
        if (!url || !/^https?:\/\//i.test(url)) {
          toast.error('Enter a valid http(s) URL');
          setUploading(false);
          return;
        }
        res = await fetch(`${BACKEND_URL}/api/learning/link`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: form.title, abstract: form.abstract, category: form.category,
            tags: form.tags, authors: form.authors,
            year: form.year || null, month: form.month || null,
            institution: form.institution || null, country: form.country || null,
            external_url: url,
          }),
        });
      } else {
        if (!form.file) { toast.error('Please select a file'); setUploading(false); return; }
        const fd = new FormData();
        fd.append('file', form.file);
        fd.append('title', form.title);
        fd.append('abstract', form.abstract);
        fd.append('category', form.category);
        fd.append('tags', form.tags.join(','));
        fd.append('authors', form.authors);
        if (form.year) fd.append('year', form.year);
        if (form.month) fd.append('month', form.month);
        if (form.institution) fd.append('institution', form.institution);
        if (form.country) fd.append('country', form.country);
        res = await fetch(`${BACKEND_URL}/api/learning/upload`, {
          method: 'POST', credentials: 'include', body: fd,
        });
      }

      if (res.ok) {
        toast.success(sourceMode === 'link' ? 'Research link added and approved!' : 'Research uploaded and auto-approved!');
        setShowUpload(false);
        setForm({ title: '', abstract: '', category: 'Academic', tags: [], authors: '', year: '', month: '', institution: '', country: '', file: null, external_url: '' });
        setSourceMode('file');
        loadResearch(researchFilter);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed'); }
    setUploading(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-research-content">
      <div className="flex gap-1 border-b border-slate-200" data-testid="learning-subnav">
        <button onClick={() => setView('docs')} data-testid="learning-subnav-docs"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === 'docs'
              ? 'border-brand-navy text-brand-navy'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}>
          <FileText className="h-3.5 w-3.5 inline mr-1.5" /> Documents
        </button>
        <button onClick={() => setView('topics')} data-testid="learning-subnav-topics"
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            view === 'topics'
              ? 'border-brand-navy text-brand-navy'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}>
          <Hash className="h-3.5 w-3.5 inline mr-1.5" /> Topics
        </button>
      </div>

      {view === 'topics' ? <AdminResearchTopicsTab /> : (
        <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <select value={researchFilter} onChange={(e) => handleFilterChange(e.target.value)}
            className="rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="learning-status-filter">
            <option value="">All</option>
            <option value="pending">Pending Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <span className="text-sm text-slate-500">{researchDocs.length} document{researchDocs.length !== 1 ? 's' : ''}</span>
        </div>
        <Button onClick={() => setShowUpload(true)} data-testid="admin-upload-research-btn" size="sm">
          <Upload className="h-4 w-4 mr-1.5" /> Upload Research
        </Button>
      </div>

      {researchDocs.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">No {researchFilter} documents</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {researchDocs.map(doc => (
            <Card key={doc.doc_id} className="p-5" data-testid={`admin-research-${doc.doc_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white ${
                      doc.category === 'Academic' ? 'bg-brand-navy' : doc.category === 'Case Study' ? 'bg-amber-600' : 'bg-brand-sage'
                    }`}>{doc.category}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                      doc.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      doc.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>{doc.status}</span>
                  </div>
                  <h3 className="font-bold text-slate-800 mb-1">{doc.title}</h3>
                  {doc.abstract && <p className="text-sm text-slate-600 line-clamp-2 mb-2">{doc.abstract}</p>}
                  <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                    {doc.authors && <span><User className="h-3 w-3 inline mr-1" />{doc.authors}</span>}
                    {(doc.month || doc.year) && <span>{[doc.month, doc.year].filter(Boolean).join(' ')}</span>}
                    {doc.uploader_name && <span>Uploaded by: {doc.uploader_name}</span>}
                    <span>{doc.original_filename}</span>
                  </div>
                  {doc.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {doc.tags.map(t => <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{t}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button className="p-2 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                    data-testid={`view-research-${doc.doc_id}`} title="View Document"
                    onClick={(e) => { e.stopPropagation(); window.open(`${BACKEND_URL}/api/learning/${doc.doc_id}/view?auth=${token}`, '_blank'); }}>
                    <Eye className="h-5 w-5" />
                  </button>
                  <button className="p-2 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-500 transition-colors"
                    data-testid={`download-research-${doc.doc_id}`} title="Download Document"
                    onClick={(e) => {
                      e.stopPropagation();
                      const iframe = document.createElement('iframe');
                      iframe.style.display = 'none';
                      iframe.src = `${BACKEND_URL}/api/learning/${doc.doc_id}/download?auth=${token}`;
                      document.body.appendChild(iframe);
                      setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 60000);
                    }}>
                    <Download className="h-5 w-5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); openEdit(doc); }}
                    className="p-2 rounded-md bg-amber-50 hover:bg-amber-100 text-amber-600 transition-colors"
                    data-testid={`edit-research-${doc.doc_id}`} title="Edit">
                    <Edit className="h-5 w-5" />
                  </button>
                  {doc.status === 'pending' && (
                    <>
                      <button onClick={() => handleAction(doc.doc_id, 'approve')}
                        className="p-2 rounded-md bg-green-50 hover:bg-green-100 text-green-600 transition-colors"
                        data-testid={`approve-${doc.doc_id}`} title="Approve">
                        <CheckCircle className="h-5 w-5" />
                      </button>
                      <button onClick={() => handleAction(doc.doc_id, 'reject')}
                        className="p-2 rounded-md bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                        data-testid={`reject-${doc.doc_id}`} title="Reject">
                        <XCircle className="h-5 w-5" />
                      </button>
                    </>
                  )}
                  <button onClick={() => handleDelete(doc.doc_id)}
                    className="p-2 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-400 transition-colors"
                    data-testid={`delete-research-${doc.doc_id}`} title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Research Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Research (Auto-Approved)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Title *</Label>
              <Input placeholder="Research document title" value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="admin-upload-title" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Abstract</Label>
              <RichTextEditor placeholder="Brief summary of the research..." value={form.abstract}
                onChange={v => setForm(p => ({ ...p, abstract: v }))} testId="admin-upload-abstract" minHeight="6rem" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Category *</Label>
                <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="admin-upload-category">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Year</Label>
                <Input placeholder="e.g. 2025" value={form.year}
                  onChange={e => setForm(p => ({ ...p, year: e.target.value }))} data-testid="admin-upload-year" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Month</Label>
                <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  value={form.month} onChange={e => setForm(p => ({ ...p, month: e.target.value }))} data-testid="admin-upload-month">
                  <option value="">-- Select Month --</option>
                  {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Authors</Label>
              <Input placeholder="Author names" value={form.authors}
                onChange={e => setForm(p => ({ ...p, authors: e.target.value }))} data-testid="admin-upload-authors" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Institution</Label>
                <Input placeholder="University or org" value={form.institution}
                  onChange={e => setForm(p => ({ ...p, institution: e.target.value }))} data-testid="admin-upload-institution" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Country</Label>
                <Input placeholder="e.g. UK" value={form.country}
                  onChange={e => setForm(p => ({ ...p, country: e.target.value }))} data-testid="admin-upload-country" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Tags * (select 1-6)</Label>
              <div className="flex flex-wrap gap-1.5">
                {TAG_OPTIONS.map(t => (
                  <button key={t} type="button" onClick={() => toggleTag(t)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      form.tags.includes(t) ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`} data-testid={`admin-upload-tag-${t}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Source *</Label>
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => setSourceMode('file')}
                  className={`flex-1 text-xs px-3 py-2 rounded-lg border transition-colors ${sourceMode === 'file' ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                  data-testid="admin-source-mode-file">
                  Upload File
                </button>
                <button type="button" onClick={() => setSourceMode('link')}
                  className={`flex-1 text-xs px-3 py-2 rounded-lg border transition-colors ${sourceMode === 'link' ? 'bg-brand-navy text-white border-brand-navy' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                  data-testid="admin-source-mode-link">
                  Link to URL
                </button>
              </div>
              {sourceMode === 'file' ? (
                <>
                  <p className="text-[10px] text-slate-400 mb-1">PDF, DOCX, PPTX, XLSX</p>
                  <Input type="file" accept=".pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.txt,.csv"
                    onChange={e => setForm(p => ({ ...p, file: e.target.files?.[0] || null }))} data-testid="admin-upload-file" />
                  {form.file && <p className="text-xs text-slate-400 mt-1">{form.file.name} ({(form.file.size / 1024 / 1024).toFixed(2)} MB)</p>}
                </>
              ) : (
                <>
                  <p className="text-[10px] text-slate-400 mb-1">For documents you can't host directly (paywalled, licensed, off-platform)</p>
                  <Input type="url" placeholder="https://example.com/whitepaper.pdf"
                    value={form.external_url}
                    onChange={e => setForm(p => ({ ...p, external_url: e.target.value }))}
                    data-testid="admin-upload-url" />
                </>
              )}
            </div>
            <Button onClick={handleUpload} disabled={uploading} className="w-full" data-testid="admin-upload-submit">
              {uploading ? 'Uploading...' : 'Upload & Approve'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Research Dialog — admin can update metadata in-place. File body
          can be re-uploaded via the regular user-side edit flow; this dialog
          covers title, abstract, category, tags and bibliographic fields. */}
      <Dialog open={!!editingDoc} onOpenChange={(o) => { if (!o) { setEditingDoc(null); setEditForm(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-edit-research-dialog">
          <DialogHeader>
            <DialogTitle>Edit Research — {editingDoc?.title?.slice(0, 50)}{editingDoc?.title?.length > 50 ? '…' : ''}</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4 pt-2">
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Title *</Label>
                <Input value={editForm.title}
                  onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                  data-testid="edit-research-title" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Abstract</Label>
                <RichTextEditor value={editForm.abstract}
                  onChange={v => setEditForm(p => ({ ...p, abstract: v }))}
                  testId="edit-research-abstract" minHeight="7rem" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Category</Label>
                  <select className="w-full rounded-md border border-slate-200 px-2 py-2 text-sm bg-white"
                    value={editForm.category}
                    onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Year</Label>
                  <Input type="number" value={editForm.year}
                    onChange={e => setEditForm(p => ({ ...p, year: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Authors</Label>
                  <Input value={editForm.authors}
                    onChange={e => setEditForm(p => ({ ...p, authors: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Month</Label>
                  <Input value={editForm.month}
                    onChange={e => setEditForm(p => ({ ...p, month: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Institution</Label>
                  <Input value={editForm.institution}
                    onChange={e => setEditForm(p => ({ ...p, institution: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Country</Label>
                  <Input value={editForm.country}
                    onChange={e => setEditForm(p => ({ ...p, country: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 mb-1.5 block">Tags (max 6)</Label>
                <div className="flex flex-wrap gap-2">
                  {TAG_OPTIONS.map(t => (
                    <button key={t} type="button" onClick={() => toggleEditTag(t)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        editForm.tags.includes(t)
                          ? 'bg-brand-navy text-white border-brand-navy'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-brand-navy/40'
                      }`}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => { setEditingDoc(null); setEditForm(null); }}>Cancel</Button>
                <Button onClick={handleSaveEdit} disabled={savingEdit}
                  className="bg-brand-navy text-white"
                  data-testid="edit-research-save-btn">
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}
